import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  DeleteSpaceAdminValidationSchema,
  type TDeleteSpaceAdminValidation,
  useDeleteSpaceAdminMutation,
} from '../../../api/admin/delete-space-admin.api';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { queryClient } from '@/lib/react-query';
import { GET_LIST_SPACE_ADMIN_QUERY_KEY } from '../../../api/admin/get-list-space-admin.api';
import { GET_LIST_SPACES_QUERY_KEY } from '../../../api/get-list-space.api';
import { GET_SINGLE_SPACE_QUERY_KEY } from '../../../api/get-single-space.api';
import { TOAST_MESSAGES_DELETE_SPACE } from './delete-space-modal.constant';
import { useTicketingModalRootActions } from '@/features/ticketings/_shared/components/ticketing-modal-root/ticketing-modal-root.store';

export interface IUseDeleteSpaceModalProps {
  spaceId: string;
}

export function useDeleteSpaceModal({ spaceId }: IUseDeleteSpaceModalProps) {
  // HOOKS
  const showToast = useCustomToast();
  const modalActions = useTicketingModalRootActions();
  const form = useForm<TDeleteSpaceAdminValidation>({
    resolver: zodResolver(DeleteSpaceAdminValidationSchema),
    mode: 'onChange',
  });

  const mutationDeleteSpace = useDeleteSpaceAdminMutation({
    mutationConfig: {
      onError: (_error) => {
        // console.log('ERROR DELETE SPACE=>', _error);
        const errorMessage = axiosExtractErrorMessage(_error) || TOAST_MESSAGES_DELETE_SPACE.DELETE_SPACE_ERROR;
        showToast({
          message: errorMessage,
          type: 'error',
        });
      },
      onSuccess: (_data) => {
        // console.log('SUCCESS DELETE SPACE=>', _data);
        Promise.all([
          queryClient.invalidateQueries({ queryKey: [GET_LIST_SPACE_ADMIN_QUERY_KEY] }),
          queryClient.invalidateQueries({ queryKey: [GET_LIST_SPACES_QUERY_KEY] }),
          queryClient.invalidateQueries({ queryKey: [GET_SINGLE_SPACE_QUERY_KEY, { spaceId: spaceId || '' }] }),
        ]);
        modalActions.closeModal('DeleteSpaceModal');
        showToast({
          message: TOAST_MESSAGES_DELETE_SPACE.DELETE_SPACE_SUCCESS,
          type: 'success',
        });
      },
    },
  });

  // HELPERS
  const isOpenDeleteSpaceModal = useMemo(() => {
    return modalActions.checkIsOpenModal('DeleteSpaceModal');
  }, [modalActions]);

  const isDisabledForm = useMemo(() => {
    return mutationDeleteSpace.isPending || form.formState.isSubmitting;
  }, [mutationDeleteSpace.isPending, form.formState.isSubmitting]);

  const isLoadingForm = useMemo(() => {
    return mutationDeleteSpace.isPending || form.formState.isSubmitting;
  }, [mutationDeleteSpace.isPending, form.formState.isSubmitting]);

  // EFFECTS
  useEffect(() => {
    if (spaceId) {
      form.reset({
        spaceId: spaceId || '',
      });
    }
  }, [spaceId, form]);

  // HANDLERS
  const handleCloseModal = useCallback(() => {
    modalActions.closeModal('DeleteSpaceModal');
  }, [modalActions]);

  const handleSubmitForm = useCallback(
    (data: TDeleteSpaceAdminValidation) => {
      mutationDeleteSpace.mutate({
        spaceId: data?.spaceId || '',
      });
    },
    [mutationDeleteSpace]
  );

  const handleClose = useCallback(() => {
    handleCloseModal();
    form.reset();
  }, [handleCloseModal, form]);

  return {
    // Form
    form,

    // Modal
    isOpenDeleteSpaceModal,

    // Helpers
    isDisabledForm,
    isLoadingForm,

    // Handlers
    handleCloseModal,
    handleSubmitForm,
    handleClose,
  };
}
